import classNames from "classnames";
import cuid from "cuid";
import dayjs from "dayjs";
import { FieldArray } from "formik";
import { get } from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  MdAdd,
  MdOutlineBadge,
  MdOutlineCake,
  MdOutlineDelete,
  MdOutlineHealthAndSafety,
  MdReportGmailerrorred,
} from "react-icons/md";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  ACORIS_SOCIAL_REGIMES,
  DATE_FORMAT,
  GENDER,
  RELATIONSHIP,
} from "utils/constants";
import { twentySevenYearsAgoStart } from "utils/helpers/date";
import {
  bindDateInputProps,
  bindInputProps,
  bindTextFieldInputProps,
} from "utils/helpers/input";
import { mapSocialRegimeOptions } from "utils/helpers/socialRegime";

export const step = { CHILDREN: "CHILDREN" };
const Children = ({ index, translation, values, ...formProps }) => {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const SOCIAL_REGIMES_TRANSLATION = t("B2C_ACORIS.SOCIAL_REGIMES", {
    returnObjects: true,
  });
  const regimeOptions = useMemo(
    () =>
      mapSocialRegimeOptions(ACORIS_SOCIAL_REGIMES, SOCIAL_REGIMES_TRANSLATION),
    [SOCIAL_REGIMES_TRANSLATION],
  );
  return (
    <div className="contact-stepper" key={step.CHILDREN}>
      <div className="contact-stepper__header">
        <div className="contact-stepper__header--counter">
          <span>{index}</span>
        </div>
        <div className="contact-stepper__header--content">
          <h1 className="contact-stepper__header--content--title">
            <span>
              {translation.TEMPLATES.CONTACT.STEPS[step.CHILDREN].TITLE}
            </span>
          </h1>
          <p className="contact-stepper__header--content--description">
            {translation.TEMPLATES.CONTACT.STEPS[step.CHILDREN].DESCRIPTION}
          </p>
        </div>
      </div>
      <div
        className={classNames("quote quote--warning", {
          hidden: values?.locked,
        })}
      >
        <MdReportGmailerrorred className="quote--icon" size={36} />
        <p className="quote--description">
          {translation.TEMPLATES.CONTACT.STEPS[step.CHILDREN].QUOTE}
        </p>
      </div>
      <div className="contact-stepper__content">
        {!values.locked || values.children.length ? (
          <FieldArray
            name={"children"}
            render={(arrayHelpers) => [
              values.children.map((child, i) => [
                <div
                  className="contact-stepper__content--box"
                  key={`child-${child.id}`}
                >
                  <div className="contact-stepper__content--box--header">
                    <h1 className="contact-stepper__content--box--header--title">
                      {`${
                        translation.TEMPLATES.CONTACT.STEPS[step.CHILDREN].FORM
                          .CHILD
                      } ${i + 1}`}
                    </h1>
                    <div
                      className="contact-stepper__content--box--header--action"
                      onClick={() => {
                        arrayHelpers.remove(i);
                      }}
                    >
                      <MdOutlineDelete size={14} />
                      <span>
                        {
                          translation.TEMPLATES.CONTACT.STEPS[step.CHILDREN]
                            .FORM.DELETE_CHILD
                        }
                      </span>
                    </div>
                  </div>
                  <Input
                    required
                    label={
                      translation.TEMPLATES.CONTACT.STEPS[step.CHILDREN].FORM
                        .LASTNAME.LABEL
                    }
                    icon={<MdOutlineBadge size={22} />}
                    placeholder={
                      translation.TEMPLATES.CONTACT.STEPS[step.CHILDREN].FORM
                        .LASTNAME.PLACEHOLDER
                    }
                    {...bindTextFieldInputProps({
                      name: `children.${i}.identity.user.lastname`,
                      values,
                      setFieldValue: formProps.setFieldValue,
                      ...formProps,
                    })}
                  />
                  <Input
                    required
                    label={
                      translation.TEMPLATES.CONTACT.STEPS[step.CHILDREN].FORM
                        .FIRSTNAME.LABEL
                    }
                    icon={<MdOutlineBadge size={22} />}
                    placeholder={
                      translation.TEMPLATES.CONTACT.STEPS[step.CHILDREN].FORM
                        .FIRSTNAME.PLACEHOLDER
                    }
                    {...bindTextFieldInputProps({
                      name: `children.${i}.identity.user.firstname`,
                      values,
                      setFieldValue: formProps.setFieldValue,
                      ...formProps,
                    })}
                  />
                  <Input
                    required
                    icon={<MdOutlineCake size={22} />}
                    label={
                      translation.TEMPLATES.CONTACT.STEPS[step.CHILDREN].FORM
                        .BIRTHDAY.LABEL
                    }
                    placeholder={
                      translation.TEMPLATES.CONTACT.STEPS[step.CHILDREN].FORM
                        .BIRTHDAY.PLACEHOLDER
                    }
                    type="date"
                    variant="simple"
                    format={DATE_FORMAT}
                    disabledDate={(current) =>
                      current > dayjs() || current < twentySevenYearsAgoStart
                    }
                    defaultPickerValue={
                      !get(values, `children.${i}.identity.user.birthDate`) &&
                      dayjs().subtract(1, "years")
                    }
                    {...bindDateInputProps({
                      name: `children.${i}.identity.user.birthDate`,
                      values,
                      ...formProps,
                    })}
                  />
                  <Input
                    type="radio"
                    variant="group"
                    required
                    label={
                      translation.TEMPLATES.CONTACT.STEPS[step.CHILDREN].FORM
                        .SEXE.LABEL
                    }
                    values={[
                      {
                        label:
                          translation.TEMPLATES.CONTACT.STEPS[step.CHILDREN]
                            .FORM.SEXE.MALE,
                        value: GENDER.MALE,
                      },
                      {
                        label:
                          translation.TEMPLATES.CONTACT.STEPS[step.CHILDREN]
                            .FORM.SEXE.FEMALE,
                        value: GENDER.FEMALE,
                      },
                    ]}
                    icon={<MdOutlineBadge size={22} />}
                    {...bindInputProps({
                      name: `children.${i}.identity.user.gender`,
                      values,
                      ...formProps,
                    })}
                  />
                  <Select
                    required
                    icon={<MdOutlineHealthAndSafety size={22} />}
                    options={regimeOptions}
                    label={
                      translation.TEMPLATES.CONTACT.STEPS[step.CHILDREN].FORM
                        .BASE_REGIME.LABEL
                    }
                    placeholder={
                      translation.TEMPLATES.CONTACT.STEPS[step.CHILDREN].FORM
                        .BASE_REGIME.PLACEHOLDER
                    }
                    {...bindInputProps({
                      name: `children.${i}.identity.socialRegime`,
                      values,
                      ...formProps,
                    })}
                  />
                </div>,
              ]),
              values.children.length < 12 && (
                <button
                  key="add-child"
                  className="button__action"
                  onClick={() => {
                    arrayHelpers.push({
                      id: cuid(),
                      relationship: RELATIONSHIP.CHILD,
                      identity: {
                        id: cuid(),
                        socialRegime: null,
                        user: {
                          id: cuid(),
                          gender: null,
                          firstname: null,
                          lastname: null,
                          birthDate: null,
                        },
                      },
                    });
                  }}
                >
                  <MdAdd size={22} />
                  {
                    translation.TEMPLATES.CONTACT.STEPS[step.CHILDREN].FORM
                      .ADD_NEW_CHILD
                  }
                </button>
              ),
            ]}
          />
        ) : (
          <p>
            {
              translation.TEMPLATES.CONTACT.STEPS[step.CHILDREN].FORM
                .NO_CHILDREN
            }
          </p>
        )}
      </div>
    </div>
  );
};
export default Children;
